import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ClientEvent, ServerEvent, type RtcSignalBroadcast } from '@tutor/realtime';
import type { RtcIceServerConfig } from '@tutor/shared';
import { getSocket } from '../lib/socket';

/**
 * Peer-to-peer audio, video and screen sharing for the classroom.
 *
 * Topology is a full mesh: every participant holds one RTCPeerConnection to every
 * other participant, and media never touches the server. That is the right trade for
 * tutoring — typically two people in a room — and avoids running an SFU. Past roughly
 * six participants a mesh becomes wasteful and this would need an SFU instead.
 *
 * The server only relays SDP and ICE (see `apps/api/src/websocket/rtc.handler.ts`)
 * and never inspects them.
 */

export interface RemotePeerMedia {
  userId: string;
  stream: MediaStream;
  hasVideo: boolean;
  hasAudio: boolean;
}

export interface UseWebRTCResult {
  localStream: MediaStream | null;
  remotePeers: RemotePeerMedia[];
  audioEnabled: boolean;
  videoEnabled: boolean;
  screenSharing: boolean;
  /** Set when the browser refused camera/mic access, so the UI can explain why. */
  mediaError: string | null;
  isRequestingMedia: boolean;

  toggleAudio: () => Promise<void>;
  toggleVideo: () => Promise<void>;
  toggleScreenShare: () => Promise<void>;
}

interface PeerEntry {
  pc: RTCPeerConnection;
  audioSender: RTCRtpSender;
  videoSender: RTCRtpSender;
  stream: MediaStream;
  /** ICE can arrive before the offer/answer it belongs to; hold it until then. */
  pendingCandidates: RTCIceCandidateInit[];
  makingOffer: boolean;
  polite: boolean;
}

export function useWebRTC(
  lessonId: string | undefined,
  ready: boolean,
  iceServers: RtcIceServerConfig[],
  onMediaStateChange: (state: {
    audioEnabled: boolean;
    videoEnabled: boolean;
    screenSharing: boolean;
  }) => void,
): UseWebRTCResult {
  const peersRef = useRef<Map<string, PeerEntry>>(new Map());
  const localAudioTrackRef = useRef<MediaStreamTrack | null>(null);
  const localVideoTrackRef = useRef<MediaStreamTrack | null>(null);
  const cameraTrackRef = useRef<MediaStreamTrack | null>(null);

  // Held in a ref, not a dependency: TanStack Query hands back a fresh `iceServers`
  // array on every refetch, and letting that identity churn into the signalling effect
  // would close and rebuild every live peer connection on window focus.
  const iceServersRef = useRef(iceServers);
  iceServersRef.current = iceServers;

  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteVersion, setRemoteVersion] = useState(0);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [videoEnabled, setVideoEnabled] = useState(false);
  const [screenSharing, setScreenSharing] = useState(false);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [isRequestingMedia, setIsRequestingMedia] = useState(false);

  const bumpRemote = useCallback(() => setRemoteVersion((v) => v + 1), []);

  const rebuildLocalStream = useCallback(() => {
    const tracks = [localAudioTrackRef.current, localVideoTrackRef.current].filter(
      (track): track is MediaStreamTrack => track !== null,
    );
    setLocalStream(tracks.length > 0 ? new MediaStream(tracks) : null);
  }, []);

  /* ------------------------------------------------------------------ */
  /* Peer connections                                                    */
  /* ------------------------------------------------------------------ */

  const createPeer = useCallback(
    (peerUserId: string, polite: boolean): PeerEntry => {
      const existing = peersRef.current.get(peerUserId);
      if (existing) return existing;

      const socket = getSocket();
      const pc = new RTCPeerConnection({ iceServers: iceServersRef.current as RTCIceServer[] });

      // Transceivers are created up front in sendrecv, so turning the camera on later
      // is a `replaceTrack` on an existing m-line rather than a renegotiation. That
      // removes the entire offer/answer glare problem from the toggle path.
      const audioTransceiver = pc.addTransceiver('audio', { direction: 'sendrecv' });
      const videoTransceiver = pc.addTransceiver('video', { direction: 'sendrecv' });

      if (localAudioTrackRef.current) void audioTransceiver.sender.replaceTrack(localAudioTrackRef.current);
      if (localVideoTrackRef.current) void videoTransceiver.sender.replaceTrack(localVideoTrackRef.current);

      const stream = new MediaStream();

      const entry: PeerEntry = {
        pc,
        audioSender: audioTransceiver.sender,
        videoSender: videoTransceiver.sender,
        stream,
        pendingCandidates: [],
        makingOffer: false,
        polite,
      };

      pc.onicecandidate = (event) => {
        if (!event.candidate || !lessonId) return;
        socket.emit(ClientEvent.RTC_SIGNAL, {
          lessonId,
          targetUserId: peerUserId,
          signal: { kind: 'candidate', candidate: event.candidate.toJSON() },
        });
      };

      // `replaceTrack` does not associate a stream, so `event.streams` can be empty.
      // Collecting tracks into our own per-peer stream works either way.
      pc.ontrack = (event) => {
        if (!stream.getTracks().some((track) => track.id === event.track.id)) {
          stream.addTrack(event.track);
        }
        event.track.addEventListener('ended', () => {
          stream.removeTrack(event.track);
          bumpRemote();
        });
        event.track.addEventListener('mute', bumpRemote);
        event.track.addEventListener('unmute', bumpRemote);
        bumpRemote();
      };

      pc.onnegotiationneeded = () => {
        void (async () => {
          if (!lessonId) return;
          try {
            entry.makingOffer = true;
            await pc.setLocalDescription();
            socket.emit(ClientEvent.RTC_SIGNAL, {
              lessonId,
              targetUserId: peerUserId,
              signal: { kind: 'offer', description: pc.localDescription },
            });
          } catch (error) {
            console.error('Negotiation failed', error);
          } finally {
            entry.makingOffer = false;
          }
        })();
      };

      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'failed') {
          // A failed connection usually means ICE could not find a path. Restarting
          // ICE is cheaper and less disruptive than tearing the whole peer down.
          pc.restartIce();
        }
        if (pc.connectionState === 'closed') {
          peersRef.current.delete(peerUserId);
          bumpRemote();
        }
      };

      peersRef.current.set(peerUserId, entry);
      bumpRemote();
      return entry;
    },
    [lessonId, bumpRemote],
  );

  const closePeer = useCallback(
    (peerUserId: string) => {
      const entry = peersRef.current.get(peerUserId);
      if (!entry) return;
      entry.pc.onicecandidate = null;
      entry.pc.ontrack = null;
      entry.pc.onnegotiationneeded = null;
      entry.pc.onconnectionstatechange = null;
      entry.pc.close();
      peersRef.current.delete(peerUserId);
      bumpRemote();
    },
    [bumpRemote],
  );

  /* ------------------------------------------------------------------ */
  /* Signalling                                                          */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    if (!lessonId || !ready) return;

    const socket = getSocket();

    const onPeerReady = (payload: { lessonId: string; userId: string; shouldInitiate: boolean }): void => {
      if (payload.lessonId !== lessonId) return;

      // The server picks the initiator deterministically (lower user id offers), so
      // two peers never send offers at each other simultaneously.
      const entry = createPeer(payload.userId, !payload.shouldInitiate);

      if (payload.shouldInitiate) {
        void (async () => {
          try {
            entry.makingOffer = true;
            await entry.pc.setLocalDescription();
            socket.emit(ClientEvent.RTC_SIGNAL, {
              lessonId,
              targetUserId: payload.userId,
              signal: { kind: 'offer', description: entry.pc.localDescription },
            });
          } catch (error) {
            console.error('Failed to create offer', error);
          } finally {
            entry.makingOffer = false;
          }
        })();
      }
    };

    const onSignal = (payload: RtcSignalBroadcast): void => {
      if (payload.lessonId !== lessonId) return;

      void (async () => {
        const { fromUserId, signal } = payload;
        const entry = peersRef.current.get(fromUserId) ?? createPeer(fromUserId, true);
        const { pc } = entry;

        try {
          if (signal.kind === 'offer' || signal.kind === 'answer') {
            const description = signal.description as RTCSessionDescriptionInit;

            // Perfect-negotiation collision handling: if we are mid-offer and an offer
            // arrives, the impolite peer ignores it and the polite peer rolls back.
            const offerCollision =
              description.type === 'offer' && (entry.makingOffer || pc.signalingState !== 'stable');

            if (offerCollision && !entry.polite) return;

            await pc.setRemoteDescription(description);

            for (const candidate of entry.pendingCandidates.splice(0)) {
              await pc.addIceCandidate(candidate).catch(() => undefined);
            }

            if (description.type === 'offer') {
              await pc.setLocalDescription();
              socket.emit(ClientEvent.RTC_SIGNAL, {
                lessonId,
                targetUserId: fromUserId,
                signal: { kind: 'answer', description: pc.localDescription },
              });
            }
            return;
          }

          if (signal.kind === 'candidate' && signal.candidate) {
            const candidate = signal.candidate as RTCIceCandidateInit;
            if (!pc.remoteDescription) entry.pendingCandidates.push(candidate);
            else await pc.addIceCandidate(candidate).catch(() => undefined);
          }
        } catch (error) {
          console.error('Failed to handle RTC signal', error);
        }
      })();
    };

    const onParticipantLeft = (payload: { lessonId: string; userId: string }): void => {
      if (payload.lessonId === lessonId) closePeer(payload.userId);
    };

    socket.on(ServerEvent.RTC_PEER_READY, onPeerReady);
    socket.on(ServerEvent.RTC_SIGNAL, onSignal);
    socket.on(ServerEvent.CLASSROOM_PARTICIPANT_LEFT, onParticipantLeft);

    // Announce only after the listeners exist, so the resulting peer-ready storm
    // cannot arrive before we are able to handle it.
    socket.emit(ClientEvent.RTC_READY, { lessonId });

    return () => {
      socket.off(ServerEvent.RTC_PEER_READY, onPeerReady);
      socket.off(ServerEvent.RTC_SIGNAL, onSignal);
      socket.off(ServerEvent.CLASSROOM_PARTICIPANT_LEFT, onParticipantLeft);

      for (const userId of [...peersRef.current.keys()]) closePeer(userId);
    };
  }, [lessonId, ready, createPeer, closePeer]);

  /* ------------------------------------------------------------------ */
  /* Local media                                                         */
  /* ------------------------------------------------------------------ */

  const publish = useCallback(
    (kind: 'audio' | 'video', track: MediaStreamTrack | null) => {
      for (const entry of peersRef.current.values()) {
        const sender = kind === 'audio' ? entry.audioSender : entry.videoSender;
        void sender.replaceTrack(track).catch((error: unknown) => {
          console.error(`Failed to publish ${kind} track`, error);
        });
      }
    },
    [],
  );

  const describeMediaError = (error: unknown): string => {
    if (!(error instanceof DOMException)) return 'We could not start your camera or microphone.';
    switch (error.name) {
      case 'NotAllowedError':
        return 'Permission was denied. Allow camera and microphone access in your browser, then try again.';
      case 'NotFoundError':
        return 'No camera or microphone was found on this device.';
      case 'NotReadableError':
        return 'Your camera or microphone is already in use by another application.';
      default:
        return 'We could not start your camera or microphone.';
    }
  };

  const toggleAudio = useCallback(async () => {
    setMediaError(null);

    if (localAudioTrackRef.current) {
      localAudioTrackRef.current.stop();
      localAudioTrackRef.current = null;
      publish('audio', null);
      setAudioEnabled(false);
      rebuildLocalStream();
      return;
    }

    try {
      setIsRequestingMedia(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      const track = stream.getAudioTracks()[0] ?? null;
      localAudioTrackRef.current = track;
      publish('audio', track);
      setAudioEnabled(true);
      rebuildLocalStream();
    } catch (error) {
      setMediaError(describeMediaError(error));
    } finally {
      setIsRequestingMedia(false);
    }
  }, [publish, rebuildLocalStream]);

  const toggleVideo = useCallback(async () => {
    setMediaError(null);

    if (localVideoTrackRef.current && !screenSharing) {
      localVideoTrackRef.current.stop();
      localVideoTrackRef.current = null;
      cameraTrackRef.current = null;
      publish('video', null);
      setVideoEnabled(false);
      rebuildLocalStream();
      return;
    }

    try {
      setIsRequestingMedia(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' },
      });
      const track = stream.getVideoTracks()[0] ?? null;

      cameraTrackRef.current = track;

      // While sharing a screen the camera stays captured but the screen keeps the wire.
      if (!screenSharing) {
        localVideoTrackRef.current = track;
        publish('video', track);
        rebuildLocalStream();
      }

      setVideoEnabled(true);
    } catch (error) {
      setMediaError(describeMediaError(error));
    } finally {
      setIsRequestingMedia(false);
    }
  }, [publish, rebuildLocalStream, screenSharing]);

  const stopScreenShare = useCallback(() => {
    const current = localVideoTrackRef.current;
    if (current && current.label.toLowerCase().includes('screen')) current.stop();

    const camera = cameraTrackRef.current;
    const restore = camera && camera.readyState === 'live' ? camera : null;

    localVideoTrackRef.current = restore;
    publish('video', restore);
    setScreenSharing(false);
    setVideoEnabled(Boolean(restore));
    rebuildLocalStream();
  }, [publish, rebuildLocalStream]);

  const toggleScreenShare = useCallback(async () => {
    setMediaError(null);

    if (screenSharing) {
      stopScreenShare();
      return;
    }

    try {
      setIsRequestingMedia(true);
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
      const track = stream.getVideoTracks()[0];
      if (!track) return;

      // Chrome's own "Stop sharing" bar bypasses our button entirely.
      track.addEventListener('ended', () => stopScreenShare());

      localVideoTrackRef.current = track;
      publish('video', track);
      setScreenSharing(true);
      setVideoEnabled(true);
      rebuildLocalStream();
    } catch (error) {
      // Cancelling the picker throws NotAllowedError; that is not a failure worth showing.
      if (error instanceof DOMException && error.name === 'NotAllowedError') return;
      setMediaError('We could not start screen sharing.');
    } finally {
      setIsRequestingMedia(false);
    }
  }, [screenSharing, stopScreenShare, publish, rebuildLocalStream]);

  // Tell the room what our media state is, so peers can label tiles correctly.
  useEffect(() => {
    onMediaStateChange({ audioEnabled, videoEnabled, screenSharing });
  }, [audioEnabled, videoEnabled, screenSharing, onMediaStateChange]);

  // Release the camera and microphone when leaving, or the device light stays on.
  useEffect(
    () => () => {
      localAudioTrackRef.current?.stop();
      localVideoTrackRef.current?.stop();
      cameraTrackRef.current?.stop();
      localAudioTrackRef.current = null;
      localVideoTrackRef.current = null;
      cameraTrackRef.current = null;
    },
    [],
  );

  const remotePeers = useMemo<RemotePeerMedia[]>(() => {
    void remoteVersion; // recomputed whenever a remote track changes
    return [...peersRef.current.entries()].map(([userId, entry]) => ({
      userId,
      stream: entry.stream,
      hasVideo: entry.stream.getVideoTracks().some((track) => track.readyState === 'live' && !track.muted),
      hasAudio: entry.stream.getAudioTracks().some((track) => track.readyState === 'live' && !track.muted),
    }));
  }, [remoteVersion]);

  return {
    localStream,
    remotePeers,
    audioEnabled,
    videoEnabled,
    screenSharing,
    mediaError,
    isRequestingMedia,
    toggleAudio,
    toggleVideo,
    toggleScreenShare,
  };
}
